/**
 * The wallet picker shown when a user clicks "Sign in".
 *
 * It lists the available wallets, each with a line on what it is, and tells newcomers they can
 * create a wallet with a passkey. Choosing one connects it and asks it to sign the sign-in
 * message. The dialog shows each step and closes once the session is open.
 */

import { LoaderCircle, Wallet, X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { type Connector, useConnectors } from "wagmi";

import { useSignIn } from "../auth/session.js";
import { describeSignInError } from "../auth/sign-in.js";
import { Notice } from "./ui.js";

/** What each kind of wallet is, for people choosing one. */
function walletHint(connector: Connector): string {
  if (connector.id === "baseAccount") {
    return "Use a Base Account, or create one with a passkey. No app or extension needed.";
  }
  if (connector.id === "injected") {
    return "The wallet extension in this browser, such as MetaMask or Rabby.";
  }
  return "The wallet extension installed in this browser.";
}

export function SignInDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const connectors = useConnectors();
  const signIn = useSignIn();
  const idPrefix = useId();

  // A native <dialog> opened with showModal() traps focus, closes on Escape and dims the page.
  useEffect(() => {
    const element = dialog.current;
    if (open && element && !element.open) {
      element.showModal();
    } else if (!open && element?.open) {
      element.close();
    }
  }, [open]);

  // The generic browser-wallet entry is only useful when no wallet announced itself by name.
  const named = connectors.filter((connector) => connector.id !== "injected");
  const wallets = named.some((connector) => connector.type === "injected") ? named : connectors;

  function choose(connector: Connector) {
    signIn.mutate(connector, { onSuccess: onClose });
  }

  return (
    <dialog
      ref={dialog}
      onClose={() => {
        signIn.reset();
        onClose();
      }}
      aria-labelledby="sign-in-title"
      className="m-auto w-[calc(100%-2rem)] max-w-md overflow-hidden rounded-3xl bg-white p-6 text-zinc-900 shadow-2xl ring-1 shadow-zinc-950/20 ring-zinc-900/10 backdrop:bg-ink-950/60 backdrop:backdrop-blur-md sm:p-7 dark:bg-ink-850 dark:text-zinc-100 dark:shadow-black/70 dark:ring-white/10 dark:backdrop:bg-black/60"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-48 w-80 -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-400/25 to-brand-600/15 blur-3xl dark:from-white/[0.06] dark:to-brand-400/[0.05]"
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-2xl bg-gradient-to-br from-brand-300 to-brand-500 text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_8px_24px_-8px_rgb(65_168_198/0.7)] dark:from-brand-400 dark:to-brand-500 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.3)]">
            <Wallet aria-hidden className="size-5" />
          </span>
          <h2 id="sign-in-title" className="font-display text-xl font-semibold tracking-[-0.02em]">
            Sign in
          </h2>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={() => dialog.current?.close()}
          className="-mt-1 -mr-2 rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-zinc-100"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      <p className="relative mt-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
        Your wallet is your Morcast account. Brands pay from it and creators are paid to it. You
        will be asked to sign a message, which is free and sends no transaction.
      </p>

      <ul className="relative mt-5 space-y-2">
        {wallets.map((connector) => {
          const nameId = `${idPrefix}-${connector.uid}-name`;
          const hintId = `${idPrefix}-${connector.uid}-hint`;
          return (
            <li key={connector.uid}>
              <button
                type="button"
                disabled={signIn.isPending}
                onClick={() => choose(connector)}
                aria-labelledby={nameId}
                aria-describedby={hintId}
                className="group flex w-full items-center gap-3 rounded-2xl bg-zinc-50/80 px-4 py-3.5 text-left ring-1 ring-zinc-900/[0.08] transition hover:bg-brand-50 hover:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white/[0.03] dark:ring-white/[0.08] dark:hover:bg-brand-400/[0.06] dark:hover:ring-brand-400/40"
              >
                {connector.icon ? (
                  <img
                    src={connector.icon}
                    alt=""
                    className="size-9 rounded-xl shadow-sm ring-1 ring-zinc-900/10 dark:ring-white/10"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="grid size-9 place-items-center rounded-xl bg-zinc-100 text-zinc-500 dark:bg-ink-800 dark:text-zinc-400"
                  >
                    <Wallet className="size-4" />
                  </span>
                )}
                <span className="min-w-0">
                  <span id={nameId} className="block text-sm font-semibold">
                    {connector.id === "injected" ? "Browser wallet" : connector.name}
                  </span>
                  <span id={hintId} className="block text-xs text-zinc-500 dark:text-zinc-400">
                    {walletHint(connector)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div aria-live="polite" className="relative mt-4 min-h-5 text-sm">
        {signIn.isPending && (
          <p className="flex items-center gap-2 text-zinc-600 dark:text-zinc-400">
            <LoaderCircle aria-hidden className="size-4 animate-spin" />
            Confirm in your wallet…
          </p>
        )}
        {signIn.isError && <Notice tone="error">{describeSignInError(signIn.error)}</Notice>}
      </div>
    </dialog>
  );
}
